import { useState, type FormEvent } from 'react';

import { Field } from '../../ui/Field.tsx';
import { describeError } from '../../ui/problemDetails.ts';
import { useCreateGroup } from './useGroups.ts';

interface CreateGroupFormProps {
  /**
   * Compact renders a settings row; otherwise a full card for the empty state, where
   * this is the only thing on the screen and deserves the room.
   */
  compact?: boolean;
  heading: string;
  description?: string;
  submitLabel: string;
}

/**
 * Creates a group.
 *
 * Used twice: as the welcome for someone with no groups at all, and inside settings for
 * someone who already has one and wants another — a household and a flatshare, or a
 * shared list with a parent. One component so the two cannot drift apart.
 */
export function CreateGroupForm({
  compact = false,
  heading,
  description,
  submitLabel,
}: CreateGroupFormProps) {
  const [name, setName] = useState('');
  const createGroup = useCreateGroup();

  const trimmed = name.trim();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (trimmed.length > 0) {
      createGroup.mutate(trimmed, { onSuccess: () => setName('') });
    }
  }

  if (compact) {
    return (
      <section className="settings-section">
        <h3 className="settings-section__title">{heading}</h3>

        <form className="settings-row" onSubmit={handleSubmit}>
          <input
            className="settings-row__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Flat 3B"
            aria-label="New group name"
            maxLength={80}
          />
          <button
            className="button button--primary button--small"
            type="submit"
            disabled={createGroup.isPending || trimmed.length === 0}
          >
            {submitLabel}
          </button>
        </form>

        {description && <p className="settings-hint">{description}</p>}

        {createGroup.isError && (
          <p className="form-error" role="alert">
            {describeError(createGroup.error)}
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="card stack">
      <h2>{heading}</h2>
      {description && <p className="muted">{description}</p>}

      <form className="stack" onSubmit={handleSubmit}>
        <Field
          label="What should it be called?"
          name="groupName"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Home"
          maxLength={80}
          required
        />

        {createGroup.isError && (
          <p className="form-error" role="alert">
            {describeError(createGroup.error)}
          </p>
        )}

        <button
          className="button button--primary"
          type="submit"
          disabled={createGroup.isPending || trimmed.length === 0}
        >
          {createGroup.isPending ? 'Creating…' : submitLabel}
        </button>
      </form>
    </section>
  );
}
